import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import styles from "./index.module.scss";

interface TableComponentProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  updateData: (rowIndex: number, columnId: keyof TData, value: string) => void;
  isEditable: boolean;
}

const AppTable = <TData extends object>({
  data,
  columns,
  updateData,
  isEditable,
}: TableComponentProps<TData>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
  });

  const isUrl = (value: string) => {
    try {
      new URL(value);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <table className={styles["table"]}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} style={{ cursor: "default" }}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id}>
                {cell.column.id === "delete" ? (
                  flexRender(cell.column.columnDef.cell, cell.getContext())
                ) : !isEditable && isUrl(cell.getValue() as string) ? (
                  <a
                    className={styles["link"]}
                    href={cell.getValue() as string}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {cell.getValue() as string}
                  </a>
                ) : (
                  <textarea
                    value={cell.getValue() as string | number}
                    onChange={(e) =>
                      updateData(row.index, cell.column.id as keyof TData, e.target.value)
                    }
                    readOnly={!isEditable}
                    style={{ width: "100%", resize: "none" }}
                  />
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default AppTable;
